/**
 * Agent resource hooks: the agent list/detail reads, agent CRUD, avatar
 * upload/clear, and template diff/apply. Template sync lives here rather than in
 * its own module because it operates on `api.agents.*` and its success handlers
 * invalidate the agent keys.
 */

import type { AgentCreateInput, AgentUpdateInput } from "@lifter/hub-ui/browser-shared";
import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useAgentsQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.agents(),
    queryFn: () => api.agents.list(),
    enabled: options.enabled ?? true,
  });
}

export function useSpecialistTemplatesQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.specialistTemplates(),
    queryFn: () => api.agents.templates(),
    enabled: options.enabled ?? true,
  });
}

export function useAgentQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.agent(agentId),
    queryFn: () => api.agents.get(agentId),
    enabled: options.enabled ?? true,
    ...(options.fresh ? { staleTime: 0, gcTime: 0 } : {}),
  });
}

export function useAgentChatTargetQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.agentChatTarget(agentId),
    queryFn: () => api.agents.chatTarget(agentId),
    enabled: options.enabled ?? true,
  });
}

export function useCreateAgentMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentCreateInput) => api.agents.create(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
      queryClient.setQueryData(hubQueryKeys.agent(data.agent.id), data);
    },
  });
}

export function useUpdateAgentMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Omit<AgentUpdateInput, "agentId">) => api.agents.update(agentId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
      queryClient.setQueryData(hubQueryKeys.agent(agentId), data);
    },
  });
}

export function useDeleteAgentMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.agents.delete(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.archivedAgents() });
    },
  });
}

export function useArchivedAgentsQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.archivedAgents(),
    queryFn: () => api.agents.listArchived(),
    enabled: options.enabled ?? true,
  });
}

export function useArchiveAgentMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.agents.archive(agentId),
    onSuccess: (_data, agentId) => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.archivedAgents() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agent(agentId) });
    },
  });
}

export function useUnarchiveAgentMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.agents.unarchive(agentId),
    onSuccess: (_data, agentId) => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.archivedAgents() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agent(agentId) });
    },
  });
}

export function useTemplateDiffQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.templateDiff(agentId),
    queryFn: () => api.agents.templateDiff(agentId),
    enabled: options.enabled ?? true,
  });
}

export function useTemplateApplyMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { forcePromptOverwrite: boolean }) =>
      api.agents.templateApply(agentId, payload),
    onSuccess: () => {
      // The agent-key prefix covers the templateDiff and skills keys too, so
      // the version pin, the prompt, the skill list, and the "Update
      // available" pill all refresh in place — no reload.
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agent(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
    },
  });
}

export function useUploadAgentAvatarMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.agents.uploadAvatar(agentId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agent(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
    },
  });
}

export function useDeleteAgentAvatarMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.agents.deleteAvatar(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agent(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
    },
  });
}
